import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe, LogOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
import { SUPPORTED_LOCALES } from '@/i18n';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { googleSignInUrl } from '@/lib/auth';

export function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const currentLocale =
    SUPPORTED_LOCALES.find((l) => l.code === i18n.language.slice(0, 2)) ?? SUPPORTED_LOCALES[0];

  const navItems = [
    { to: '/quests',      label: t('nav.quests') },
    { to: '/leaderboard', label: t('nav.leaderboard') },
    { to: '/past',        label: t('nav.past') },
    { to: '/donors',      label: t('nav.donors') },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-kquest-950/80 border-b-2 border-kquest-700">
      <div className="container-wide flex items-center justify-between h-16 px-6 sm:px-10 lg:px-16">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8 font-pixel text-sm">
          {navItems.map((it) => {
            const active = location.pathname === it.to;
            return (
              <Link
                key={it.to}
                to={it.to}
                className={cn(
                  'transition-colors uppercase tracking-wider',
                  active ? 'text-gold-400' : 'text-kquest-200 hover:text-kquest-100'
                )}
              >
                {it.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm
                         text-kquest-200 hover:bg-kquest-800 transition-colors font-pixel"
            >
              <Globe className="w-4 h-4" />
              <span>{currentLocale.flag} {currentLocale.code.toUpperCase()}</span>
            </button>
            <AnimatePresence>
              {langOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="absolute right-0 mt-2 w-52 kblock max-h-72 overflow-y-auto z-50"
                >
                  {SUPPORTED_LOCALES.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangOpen(false);
                      }}
                      className={cn(
                        'w-full text-left px-4 py-2 text-sm flex items-center gap-3 hover:bg-kquest-700 transition-colors',
                        lang.code === currentLocale.code && 'text-gold-400'
                      )}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link to="/donate" className="btn-pixel bg-redstone-500 text-white border-2 border-redstone-700 hover:bg-redstone-400 !px-4 !py-2 text-xs flex items-center gap-1.5"
                style={{ boxShadow: 'inset 0 -3px 0 0 rgba(0,0,0,0.35), 4px 4px 0 0 #7A0E0E' }}>
            ♥ {t('nav.donate')}
          </Link>
          <Link to="/submit" className="btn-gold !px-5 !py-2 text-xs">
            {t('nav.submit')}
          </Link>

          {user ? (
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-kquest-800 transition-colors"
                title={user.youtube_channel_title ?? user.username}
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-sm border-2 border-kquest-500" />
                ) : (
                  <div className="w-7 h-7 bg-kquest-700 border-2 border-kquest-500 flex items-center justify-center font-pixel text-xs">
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                )}
                <span className="font-pixel text-xs text-kquest-100 max-w-[120px] truncate">
                  {user.youtube_channel_title ?? user.username}
                </span>
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 w-72 kblock z-50 p-2"
                  >
                    {user.tier_active && user.tier && (
                      <div className="px-3 py-2 mb-2 rounded
                                      bg-violet-500/10 border border-violet-500/40">
                        <div className="font-press text-[10px] text-violet-200 uppercase tracking-wider">
                          {user.tier === 're' ? '👑 Re' : user.tier === 'cavaliere' ? '⚔ Cavaliere' : '✨ Apprendista'}
                        </div>
                        <div className="mt-2 grid grid-cols-3 gap-1 text-center text-[10px] font-pixel">
                          <div>
                            <div className="text-violet-200 text-base font-press">{user.tier_votes_remaining ?? 0}</div>
                            <div className="text-kquest-400 uppercase">voti</div>
                          </div>
                          <div>
                            <div className="text-violet-200 text-base font-press">{user.tier_quests_remaining ?? 0}</div>
                            <div className="text-kquest-400 uppercase">quest</div>
                          </div>
                          <div>
                            <div className="text-violet-200 text-base font-press">{user.tier_veto_remaining ?? 0}</div>
                            <div className="text-kquest-400 uppercase">veto</div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="px-3 py-2 text-xs font-pixel text-kquest-300 border-b border-kquest-700 mb-2">
                      {user.credits_cents > 0
                        ? <>Crediti: <span className="text-gold-400">€{(user.credits_cents/100).toFixed(2)}</span></>
                        : <>Nessun credito · <Link to="/donate" className="text-violet-300 hover:underline">Abbonati</Link></>
                      }
                    </div>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-redstone-700 transition-colors font-pixel"
                    >
                      <LogOut className="w-4 h-4" /> Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <a href={googleSignInUrl()} className="btn-purple !px-4 !py-2 text-xs flex items-center gap-2">
              <GoogleIcon className="w-4 h-4" />
              <span>Sign in</span>
            </a>
          )}
        </div>

        <button
          className="md:hidden text-kquest-100"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Menu"
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden border-t-2 border-kquest-700 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-3 font-pixel">
              {navItems.map((it) => (
                <Link
                  key={it.to}
                  to={it.to}
                  onClick={() => setMobileOpen(false)}
                  className="py-2 text-kquest-200 hover:text-gold-400 uppercase tracking-wider"
                >
                  {it.label}
                </Link>
              ))}
              <Link to="/donate" onClick={() => setMobileOpen(false)} className="btn-pixel bg-redstone-500 text-white border-2 border-redstone-700 !px-5 !py-2 text-xs justify-center flex items-center gap-2 mt-2"
                    style={{ boxShadow: 'inset 0 -3px 0 0 rgba(0,0,0,0.35), 4px 4px 0 0 #7A0E0E' }}>
                ♥ {t('nav.donate')}
              </Link>
              <Link to="/submit" onClick={() => setMobileOpen(false)} className="btn-gold !px-5 !py-2 text-xs justify-center">
                {t('nav.submit')}
              </Link>

              {user ? (
                <div className="kblock p-3 mt-2 flex items-center gap-3">
                  {user.avatar_url
                    ? <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-sm border-2 border-kquest-500" />
                    : <div className="w-8 h-8 bg-kquest-700 border-2 border-kquest-500 flex items-center justify-center font-pixel">{user.username.charAt(0).toUpperCase()}</div>}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-pixel truncate">{user.youtube_channel_title ?? user.username}</div>
                    <div className="text-[10px] text-kquest-400">€{(user.credits_cents/100).toFixed(2)} · crediti</div>
                  </div>
                  <button onClick={logout} className="text-kquest-400 hover:text-redstone-400" title="Logout">
                    <LogOut className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <a href={googleSignInUrl()} className="btn-purple !px-5 !py-2 text-xs justify-center flex items-center gap-2 mt-2">
                  <GoogleIcon className="w-4 h-4" />
                  Sign in with Google
                </a>
              )}
              <div className="grid grid-cols-3 gap-2 mt-3 pb-2">
                {SUPPORTED_LOCALES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    className={cn(
                      'px-2 py-1 text-xs flex items-center justify-center gap-1 border-2',
                      lang.code === currentLocale.code
                        ? 'bg-kquest-600 border-kquest-400 text-white'
                        : 'border-kquest-700 text-kquest-200 hover:bg-kquest-800'
                    )}
                  >
                    <span>{lang.flag}</span>
                    <span>{lang.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3c-2.1 1.6-4.7 2.5-7.3 2.5-5.1 0-9.5-3.3-11.2-7.9l-6.5 5C9.6 39.5 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.2 5.5l6.2 5.3C41 35.5 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/>
    </svg>
  );
}
